import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { rememberPostLoginPath } from "@/lib/redirect";
import { marketingUrl } from "@/lib/appLink";
import { LegalLinks } from "@/components/LegalLinks";

/* Home is the public site, not the dashboard root: everything behind this page
   needs a session, so "/" would only bounce back to this form. Plain links, so
   the way out of the login stays in the same tab. */
const HOME_URL = marketingUrl("/");

export default function Login() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [devEmail, setDevEmail] = useState("qa@complaudax.dev");
  const [params] = useSearchParams();
  const devEnabled = process.env.REACT_APP_DEV_LOGIN === "true";

  // Where the user was headed before the login wall, typically the extension
  // authorize screen. Parked in session storage as well, because Supabase falls
  // back to the site root when a redirect path is not on its allowlist.
  const next = useMemo(() => rememberPostLoginPath(params.get("next")), [params]);
  const returnTo = `${window.location.origin}${next || "/"}`;

  /* Signing in is not where consent is collected. Both routes below leave the
     page before an account exists, so nothing ticked here could be attached to
     a user or stored; the checkbox that used to sit above these buttons blocked
     every returning user and recorded nothing. Acceptance is asked for once on
     the first protected render instead, by components/TermsGate.jsx, and read
     back from the profile on later visits. */

  const googleSignIn = async () => {
    // scopes limited to email and profile by design
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { scopes: "email profile", redirectTo: returnTo },
    });
    if (error) toast.error(error.message);
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    setSending(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: returnTo },
    });
    setSending(false);
    if (error) toast.error(error.message);
    else toast.success("Magic link sent. Check your inbox.");
  };

  const devSignIn = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/dev/login", { email: devEmail });
      const { error } = await supabase.auth.setSession(data);
      if (error) throw error;
      window.location.href = next || "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <main className="min-h-screen bg-[#F8F9FA] flex">
      <div className="hidden lg:block w-1/2 border-r border-gray-200 relative">
        <img
          src="https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#16324F]/70" aria-hidden="true"></div>
        {/* Outlined, so the way out never competes with the sign-in buttons on
            the other half. White border and text: it sits on the navy wash. */}
        <a
          href={HOME_URL}
          data-testid="login-back-home-link"
          className="absolute top-12 left-12 inline-flex items-center gap-2 rounded-sm border border-white/70 px-3.5 py-2 text-sm font-medium text-white hover:bg-white/10 hover:border-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#16324F]"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back Home
        </a>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-mono text-xs uppercase tracking-widest mb-3">Accessibility 
            conformance, on the record</p>
          <h2 className="font-heading text-3xl font-semibold leading-snug">
            Audit any page. Track every fix. Deliver the document regulators ask for.
          </h2>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          {/* The alt text carries the destination: it is the link's whole
              accessible name, and "Complaudax" alone would not say where it
              goes. Below the lg breakpoint the left panel is hidden, so this is
              also the only way home. */}
          <a
            href={HOME_URL}
            data-testid="login-logo-link"
            className="inline-block rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2"
          >
            <img src="/Complaudax_logo.svg" alt="Complaudax home" className="h-8 w-auto" />
          </a>
          <p className="text-sm text-ink-muted mt-1 mb-6">Sign in to your workspace.</p>

          <button
            data-testid="google-signin-btn"
            onClick={googleSignIn}
            className="w-full bg-[#16324F] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#10243a] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2"
          >
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <hr className="flex-1 border-gray-200" />
            <span className="text-xs text-ink-subtle font-mono">or</span>
            <hr className="flex-1 border-gray-200" />
          </div>

          <form onSubmit={sendMagicLink}>
            <label htmlFor="email" className="block text-sm font-medium mb-1.5">Email address</label>
            <input
              id="email"
              data-testid="magic-link-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@agency.com"
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#16324F] focus:border-[#16324F] outline-none transition-colors"
            />
            <button
              data-testid="magic-link-submit-btn"
              type="submit"
              disabled={sending}
              className="w-full mt-3 bg-white border border-gray-300 text-ink rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2"
            >
              {sending ? "Sending..." : "Send magic link"}
            </button>
          </form>

          {/* Notice, not a control: it names what continuing means and links the
              documents, and the acceptance itself is collected and recorded once
              signed in. Below both routes because it applies to both. */}
          <p className="text-xs text-ink-muted mt-6" data-testid="login-legal-notice">
            By continuing you agree to the <LegalLinks />.
          </p>

          {devEnabled && (
            <form onSubmit={devSignIn} className="mt-8 border border-dashed border-gray-300 rounded-sm p-4">
              <p className="text-xs font-mono text-ink-muted mb-2">Development sign-in (preview only)</p>
              <div className="flex gap-2">
                <label htmlFor="dev-email" className="sr-only">Development email</label>
                <input
                  id="dev-email"
                  data-testid="dev-login-email-input"
                  type="email"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-sm px-2 py-1.5 text-xs font-mono focus:ring-2 focus:ring-[#16324F] outline-none"
                />
                <button
                  data-testid="dev-login-btn"
                  type="submit"
                  className="border border-gray-300 rounded-sm px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]"
                >
                  Sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
